/**
 * Scheduled messages (Slack "Schedule message", contracts scheduled.ts): the composer send menu's presets, the time
 * check, and the lists (per conversation and all), soonest first. Times use the person's local offset (`offsetMinutes`,
 * minutes to add to UTC), so every platform gets the same answers.
 */
import { SCHEDULE_MAX_AHEAD_MS, type ScheduledMessage } from "@bundle/contracts";
import { MORNING_HOUR, presetTime } from "./later.ts";

const MIN = 60_000;
const DAY = 24 * 60 * MIN;

export type SchedulePreset = { id: "tomorrow" | "monday"; at: number };

/**
 * Slack's send menu: "Tomorrow at 9:00 AM" and "Monday at 9:00 AM" (local), then "Custom time". Monday is left out
 * when it is tomorrow.
 */
export function schedulePresets(now: number, offsetMinutes: number): SchedulePreset[] {
  const tomorrow = presetTime("tomorrow", now, offsetMinutes);
  const monday = presetTime("nextWeek", now, offsetMinutes);
  return monday === tomorrow ? [{ id: "tomorrow", at: tomorrow }] : [{ id: "tomorrow", at: tomorrow }, { id: "monday", at: monday }];
}

/** Why a send time can't be used: in the past (or now), or more than 120 days ahead. undefined = fine. */
export function sendAtProblem(at: number, now: number): "past" | "tooFar" | undefined {
  if (at <= now) return "past";
  if (at - now > SCHEDULE_MAX_AHEAD_MS) return "tooFar";
  return undefined;
}

/** A custom pick: that local day ("YYYY-MM-DD") at `minutes` after local midnight → a UTC timestamp. */
export function localTime(day: string, minutes: number, offsetMinutes: number): number | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (m === null || minutes < 0 || minutes >= 24 * 60) return undefined;
  const utc = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (new Date(utc).getUTCDate() !== Number(m[3])) return undefined;
  return utc + minutes * MIN - offsetMinutes * MIN;
}

/** Soonest first (ties by id). */
export function sortScheduled(list: readonly ScheduledMessage[]): ScheduledMessage[] {
  return [...list].sort((a, b) => a.sendAt - b.sendAt || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/** One conversation's scheduled messages: its channel-level ones, or one thread's replies (`threadRootId`). */
export function forConversation(list: readonly ScheduledMessage[], channelId: string, threadRootId?: string): ScheduledMessage[] {
  return sortScheduled(list.filter((s) => s.channelId === channelId && s.threadRootId === threadRootId));
}

export function upsertScheduled(list: readonly ScheduledMessage[], s: ScheduledMessage): ScheduledMessage[] {
  return sortScheduled([s, ...list.filter((x) => x.id !== s.id)]);
}

export function removeScheduled(list: readonly ScheduledMessage[], id: string): ScheduledMessage[] {
  return list.filter((x) => x.id !== id);
}

/** Default custom pick: tomorrow, 9:00 local, as ["YYYY-MM-DD", minutes]. */
export function defaultCustom(now: number, offsetMinutes: number): [string, number] {
  const local = new Date(now + offsetMinutes * MIN + DAY);
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
  return [`${local.getUTCFullYear()}-${pad(local.getUTCMonth() + 1)}-${pad(local.getUTCDate())}`, MORNING_HOUR * 60];
}
